import React, { useEffect, useState, useMemo } from "react";
import useDashboard from "./useHooks";

import {
  ClipboardCloseIcon,
  ExperiencesIcon,
  FamilyProfilesIcon,
  FrameIcon,
  LearnersIcon,
  LocationTickIcon,
  MagicStarIcon,
  StarIcon,
  StarsIcon,
} from "assets/images/dashboard/svg";
import { CITY_OPTIONS, ENGAGEMENT_LEVELS, INDUSTRY_TYPES } from "utils/helpers/enums/shared.enum";
import DataNotFound from "components/particles/table/data-not-found";
import { useSelector } from "react-redux";
import useStore from "hooks/useStore";
import { sitePermissions } from "utils/helpers/enums/permissions.enum";
import statesData from "components/dummy-data/states.json";

const DashboardHome: React.FC = () => {
  const {
    getDashboard,
    getPopularStats,
    getNumberOfAttendees,
    getNumberOfEventsInLocation,
    getNumberOfAmbassador,
    getNumberOfBusinesses,
    getNumberOfBusinessesInIndustry,
    getNumberOfBusinessesInEachMembership,
    getCancellationRateByHost,
    getAmbassadorInEachEngagementLevel,
    getAmbassadorInEachTier,
  } = useDashboard();

  const { hasPermission } = useStore();
  const hasUpdatePermission = hasPermission(sitePermissions.dashboardUpdate);

  const { isLoading } = useSelector((state: any) => state.sharedReducer);

  const [selectedValues, setSelectedValues] = useState<{
    [key: string]: string;
  }>({});

  const [dashboardData, setDashboardData] = useState<any>(null);
  const [citiesLoading, setCitiesLoading] = useState<boolean>(true);
  const [citiesList, setCitiesList] = useState<string[]>([]);
  const [citySearchTerm, setCitySearchTerm] = useState<string>("");
  const [showCityDropdown, setShowCityDropdown] = useState<boolean>(false);

  // Extract all cities from states.json with loading state
  useEffect(() => {
    const extractCities = () => {
      setCitiesLoading(true);
      try {
        if (!statesData?.states) {
          setCitiesList([]);
          setCitiesLoading(false);
          return;
        }
        const cities: string[] = [];
        statesData.states.forEach((state: any) => {
          if (state.cities && Array.isArray(state.cities)) {
            state.cities.forEach((city: any) => {
              if (city.name && !cities.includes(city.name)) {
                cities.push(city.name);
              }
            });
          }
        });
        setCitiesList(cities.sort()); // Sort alphabetically
      } catch (error) {
        console.error("Error extracting cities:", error);
        setCitiesList([]);
      } finally {
        setCitiesLoading(false);
      }
    };
    extractCities();
  }, []);

  // Filter cities based on search term
  const filteredCities = useMemo(() => {
    if (!citySearchTerm) return citiesList;
    return citiesList.filter((city) =>
      city.toLowerCase().includes(citySearchTerm.toLowerCase())
    );
  }, [citySearchTerm, citiesList]);

  useEffect(() => {
    const fetchData = async () => {
      await getDashboard(setDashboardData);
    };
    fetchData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.city-dropdown-container')) {
        setShowCityDropdown(false);
      }
    };

    if (showCityDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCityDropdown]);



  const [popularStats, setPopularStats] = useState<any>(null);
  const [dashboardNumberOfAttendees, setDashboardNumberOfAttendees] = useState<any>(null);
  const [numberOfEventsInLocation, setNumberOfEventsInLocation] = useState<any>(null);
  const [numberOfBusinesses, setNumberOfBusinesses] = useState<any>(null);
  const [numberOfBusinessesInIndustry, setNumberOfBusinessesInIndustry] = useState<any>(null);
  const [numberOfBusinessesInEachMembership, setNumberOfBusinessesInEachMembership] = useState<any>(null);
  const [cancellationRateByHost, setCancellationRateByHost] = useState<any>(null);
  const [ambassadorInEachEngagementLevel, setAmbassadorInEachEngagementLevel] = useState<any>(null);
  const [ambassadorInEachTier, setAmbassadorInEachTier] = useState<any>(null);
  const [ambassadorCounts, setAmbassadorCounts] = useState<any>(null);

  const [filters, setFilters] = useState<any>(null);

  const getPopularStatsCount = (filters: any) => {
    const filtersToSend: any = {};
    for (let key in filters) {
      if (filters[key]) filtersToSend[key] = filters[key];
    }
    const queryParams = {
      ...filtersToSend,
    };
    getPopularStats(
      setPopularStats,
      queryParams,
      setFilters
    );
  };

  const getAttendeesCount = (filters: any) => {
    const filtersToSend: any = {};
    for (let key in filters) {
      if (filters[key]) filtersToSend[key] = filters[key];
    }
    const queryParams = {
      ...filtersToSend,
    };
    getNumberOfAttendees(
      setDashboardNumberOfAttendees,
      queryParams,
      setFilters
    );
  };

  const getEventLocation = (filters: any) => {
    const filtersToSend: any = {};
    for (let key in filters) {
      if (filters[key]) filtersToSend[key] = filters[key];
    }
    const queryParams = {
      ...filtersToSend,
    };
    getNumberOfEventsInLocation(
      setNumberOfEventsInLocation,
      queryParams,
      setFilters
    );
  };

  const getAmbassadorCount = (filters: any) => {
    const filtersToSend: any = {};
    for (let key in filters) {
      if (filters[key]) filtersToSend[key] = filters[key];
    }
    const queryParams = {
      ...filtersToSend,
    };
    getNumberOfAmbassador(
      setAmbassadorCounts,
      queryParams,
      setFilters
    );
  };

  const getBusinessCount = (filters: any) => {
    const filtersToSend: any = {};
    for (let key in filters) {
      if (filters[key]) filtersToSend[key] = filters[key];
    }
    const queryParams = {
      ...filtersToSend,
    };
    getNumberOfBusinesses(
      setAmbassadorCounts,
      queryParams,
      setFilters
    );
  };

  const getBusinessesInIndustryCount = (filters: any) => {
    const filtersToSend: any = {};
    for (let key in filters) {
      if (filters[key]) filtersToSend[key] = filters[key];
    }
    const queryParams = {
      ...filtersToSend,
    };
    getNumberOfBusinessesInIndustry(
      setNumberOfBusinessesInIndustry,
      queryParams,
      setFilters
    );
  };

  const getBusinessesInEachMembershipCount = (filters: any) => {
    const filtersToSend: any = {};
    for (let key in filters) {
      if (filters[key]) filtersToSend[key] = filters[key];
    }
    const queryParams = {
      ...filtersToSend,
    };
    getNumberOfBusinessesInEachMembership(
      setNumberOfBusinessesInEachMembership,
      queryParams,
      setFilters
    );
  };

  const getCancellationRateByHostCount = (filters: any) => {
    const filtersToSend: any = {};
    for (let key in filters) {
      if (filters[key]) filtersToSend[key] = filters[key];
    }
    const queryParams = {
      ...filtersToSend,
    };
    getCancellationRateByHost(
      setCancellationRateByHost,
      queryParams,
      setFilters
    );
  };

  const getAmbassadorInEachEngagementLevelCount = (filters: any) => {
    const filtersToSend: any = {};
    for (let key in filters) {
      if (filters[key]) filtersToSend[key] = filters[key];
    }
    const queryParams = {
      ...filtersToSend,
    };
    getAmbassadorInEachEngagementLevel(
      setAmbassadorInEachEngagementLevel,
      queryParams,
      setFilters
    );
  };

  const getAmbassadorInEachTierCount = (filters: any) => {
    const filtersToSend: any = {};
    for (let key in filters) {
      if (filters[key]) filtersToSend[key] = filters[key];
    }
    const queryParams = {
      ...filtersToSend,
    };
    getAmbassadorInEachTier(
      setAmbassadorInEachTier,
      queryParams,
      setFilters
    );
  };











  useEffect(() => {
    if (!filters) return;
    if (Object.keys(filters).length > 0) {
      getAttendeesCount(filters);
    }

    if (filters.city) getEventLocation(filters);
    if (filters.filter) getAmbassadorCount(filters);
    if (filters.filter) getBusinessCount(filters);
    if (filters.industry) getBusinessesInIndustryCount(filters);
    if (filters.membership) getBusinessesInEachMembershipCount(filters);
    if (filters.filter) getBusinessesInEachMembershipCount(filters);
    if (filters.filter) getCancellationRateByHostCount(filters);
    if (filters.engagementLevel) getAmbassadorInEachEngagementLevelCount(filters);
    if (filters.tier) getAmbassadorInEachTierCount(filters);

  }, []);




  const handleSelectChange = (title: string, value: string) => {
    setSelectedValues((prev) => ({ ...prev, [title]: value }));

    if (title === "Experiences") {
      // If "Total Experiences" is selected, reset to main API value
      if (value === "Total Experiences" || value === "total-experiences") {
        setPopularStats(null);
        setFilters({});
      } else {
        // Only call API for other dropdown options
        const updatedFilters = { filter: value };
        setFilters(updatedFilters);

        getPopularStats(
          setPopularStats,
          updatedFilters,
          setFilters
        );
      }
    }

    if (title === "Family Profiles") {
      const updatedFilters = { filter: value };
      setFilters(updatedFilters);

      getNumberOfAttendees(
        setDashboardNumberOfAttendees,
        updatedFilters,
        setFilters
      );

    }

    if (title === "Locations") {
      const updatedFilters = { city: value };
      setFilters(updatedFilters);

      getNumberOfEventsInLocation(
        setNumberOfEventsInLocation,
        updatedFilters,
        setFilters
      );

    }

    if (title === "No. of Ambassadors") {
      const updatedFilters = { filter: value };
      setFilters(updatedFilters);

      getNumberOfAmbassador(
        setAmbassadorCounts,
        updatedFilters,
        setFilters
      );
    }
    if (title === "Total Businesses") {
      const updatedFilters = { filter: value };
      setFilters(updatedFilters);

      getNumberOfBusinesses(
        setNumberOfBusinesses,
        updatedFilters,
        setFilters
      );
    }

    if (title === "No. of Businesses in Industries") {
      const updatedFilters = { industry: value };
      setFilters(updatedFilters);

      getNumberOfBusinessesInIndustry(
        setNumberOfBusinessesInIndustry,
        updatedFilters,
        setFilters
      );
    }

    if (title === "How many Businesses in Each type of Membership") {
      const updatedFilters = { membership: value };
      setFilters(updatedFilters);

      getNumberOfBusinessesInEachMembership(
        setNumberOfBusinessesInEachMembership,
        updatedFilters,
        setFilters
      );
    }

    if (title === "Cancellation Rate of Events by Host") {
      const updatedFilters = { filter: value };
      setFilters(updatedFilters);

      getCancellationRateByHost(
        setCancellationRateByHost,
        updatedFilters,
        setFilters
      );
    }

    if (title === "Level of Engagement of Ambassadors") {
      const updatedFilters = { engagementLevel: value };
      setFilters(updatedFilters);

      getAmbassadorInEachEngagementLevel(
        setAmbassadorInEachEngagementLevel,
        updatedFilters,
        setFilters
      );
    }

    if (title === "How many Ambassadors in Each type of Tier") {
      const updatedFilters = { tier: value };
      setFilters(updatedFilters);

      getAmbassadorInEachTier(
        setAmbassadorInEachTier,
        updatedFilters,
        setFilters
      );
    }
  };



  const cards: any[] = [
    {
      title: "Experiences",
      value: popularStats !== null
        ? (typeof popularStats === 'object' 
            ? (popularStats?.stat || '').replace(/_/g, ' ')
            : (popularStats || '').replace(/_/g, ' '))
        : dashboardData?.numberOfEvents ?? "0",

      description: "Total Experiences",
      icon: <ExperiencesIcon />,
      bgColor: "bg-[#EDF5FF]",
      dropdownOptions: [
        "Total Experiences",
        "popular-dates",
        "popular-times",
        "popular-interest",
        "popular-skill-level",
        "popular-age-group",
      ],
    },
    {
      title: "Family Profiles",
      value:
        dashboardNumberOfAttendees !== null
          ? dashboardNumberOfAttendees
          : dashboardData?.numberOfAttendees ?? "0",

      description: "All Time",
      icon: <FamilyProfilesIcon />,
      dropdownOptions: [
        "All Time",
        "Current Month",
        "Last Month",
        "Current Year",
        "Last Year",
      ],
      bgColor: "bg-[#EDF5FF]",
    },
    {
      title: "No. of Exceptional Learner Events",
      value: dashboardData?.numberOfEventsForExceptionalLearner ?? "0",
      description: "All Levels",
      icon: <LearnersIcon />,
      dropdownOptions: [
        "Exceptional Learners",
        "Appropriate for Age Level",
        "All Learners",
      ],
      bgColor: "bg-[#EDF5FF]",
    },
    {
      title: "Locations",
      value: numberOfEventsInLocation !== null
        ? numberOfEventsInLocation
        : dashboardData?.numberOfEventsInLocation ?? "0",
      description: "Select City",
      icon: <LocationTickIcon />,
      dropdownOptions: citiesList.length > 0 ? citiesList : Object.values(CITY_OPTIONS),
      isSearchable: true, // Flag to identify Locations card

      bgColor: "bg-[#FFF6E4]",
    },
    // NOTE: Not implemented (field not present in main /dashboard response + no dedicated endpoint wired)
    // {
    //   title: "Cancellation by Attendees",
    //   value: dashboardData?.numberOfCancellations ?? "0",
    //   description: "All Time",
    //   icon: <ClipboardCloseIcon />,
    //   bgColor: "bg-[#FFEDED]",
    //   dropdownOptions: [
    //     "All Time",
    //     "Current Month",
    //     "Last Month",
    //     "Current Year",
    //     "Last Year",
    //   ],
    // },
    {
      title: `Average Session Attended per Family (Current Month)`,
      value: dashboardData?.averageAttendanceRate ? `${dashboardData.averageAttendanceRate}%` : "0%",
      // description: "5",
      // icon: <FaStar />,
      // dropdownOptions: ["5", "10", "15", "20", "25"],
      bgColor: "bg-[#FFEDFF]",
    },
    // NOTE: Not implemented (hardcoded value, no API wired)
    // {
    //   title: `Attendee Survey Completion Rate (Feedback)`,
    //   value: "77%",
    //   icon: <StarIcon />,
    //   bgColor: "bg-[#EDF5FF]",
    // },
    {
      title: "Attendee Satisfaction Rate (Overall Rating)",
      value: dashboardData?.attendeeSatisfaction ?? "0",
      description: "Overall Rating",
      icon: <MagicStarIcon />,
      bgColor: "bg-[#EDF5FF]",
    },
    {
      title: "Total Businesses",
      value: numberOfBusinesses !== null
        ? numberOfBusinesses
        : dashboardData?.numberOfBusinesses ?? "0",
      description: "All Time",
      icon: <StarsIcon />,
      bgColor: "bg-[#FFF6E4]",
      dropdownOptions: [
        "All Time",
        "Current Month",
        "Last Month",
        "Current Year",
        "Last Year",
      ],
    },
    {
      title: "No. of Businesses in Industries",
      value: numberOfBusinessesInIndustry !== null
        ? numberOfBusinessesInIndustry
        : dashboardData?.numberOfBusinessesInIndustry ?? "0",
      description: "Select Industry",
      icon: <StarsIcon />,
      dropdownOptions: Object.values(INDUSTRY_TYPES),
      bgColor: "bg-[#FFF6E4]",
    },
    {
      title: "How many Businesses in Each type of Membership",
      value: numberOfBusinessesInEachMembership !== null
        ? numberOfBusinessesInEachMembership
        : dashboardData?.numberOfBusinessesInEachMembership ?? "0",
      description: "Basic Membership",
      icon: <StarsIcon />,

      dropdownOptions: [
        "BASIC",
        "TIER_1",
        "TIER_2",
      ],
      bgColor: "bg-[#FFF6E4]",
    },
    {
      title: "Cancellation Rate of Events by Host",
      value: cancellationRateByHost !== null
        ? cancellationRateByHost
        : dashboardData?.cancelRateOfEventsByHost ?? "0",
      description: "All Time",
      icon: <ClipboardCloseIcon />,

      bgColor: "bg-[#FFEDED]",
      dropdownOptions: [
        "All Time",
        "Current Month",
        "Last Month",
        "Current Year",
        "Last Year",
      ],
    },
    {
      title: "How many Ambassadors in Each type of Tier",
      value: ambassadorInEachTier !== null
        ? ambassadorInEachTier
        : dashboardData?.numberOfAmbassadorInTier ?? "0",

      description: "Community Builder",
      icon: <FrameIcon />,
      dropdownOptions: [
        "tierOne",
        "tierTwo",
        "tierThree",
      ],
      bgColor: "bg-[#E2DAF8]",
    },
    {
      title: "No. of Ambassadors",
      value: ambassadorCounts !== null
        ? ambassadorCounts
        : dashboardData?.numberOfAmbassador ?? "0",
      description: "All Time",
      icon: <FrameIcon />,
      bgColor: "bg-[#E2DAF8]",
      dropdownOptions: [
        "All Time",
        "Current Month",
        "Last Month",
        "Current Year",
        "Last Year",
      ],
    },
    {
      title: "Level of Engagement of Ambassadors",
      value: ambassadorInEachEngagementLevel !== null
        ? ambassadorInEachEngagementLevel
        : dashboardData?.numberOfAmbassadorInEngagementLevel ?? "0",
      description: "Minimum",
      icon: <FrameIcon />,
      dropdownOptions: [
        "MINIMUM_5_6_HOURS_PER_MONTH",
        "MODERATE_10_15_HOURS_PER_MONTH",
        "HIGH_15_PLUS_HOURS_PER_MONTH",
      ],
      bgColor: "bg-[#E2DAF8]",
    },
    // NOTE: Not implemented (hardcoded value, no API wired)
    // {
    //   title: "Top Ambassador Performer of the Month",
    //   value: "240",
    //   description: "Community Builder",
    //   icon: <FrameIcon />,
    //   bgColor: "bg-[#E2DAF8]",
    //   dropdownOptions: [
    //     "Community Builder ",
    //     "Community Advocate",
    //     "Community Leader",
    //   ],
    // },
  ];

  return (
    <div className="">
      <h1 className="tex-2xl sm:text-[32px] text-primaryText font-bold">
        Dashboard
      </h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 my-6">
        {cards.map((card, index) => (
          <div
            key={index}
            className={`p-3 rounded-xl flex flex-col justify-between gap-2 relative h-[161px] ${card.bgColor}`}
          >
            <div className="flex justify-between items-start gap-2">
              <div className="text-sm text-primaryText font-semibold opacity-80">
                {card.title}
              </div>
              {card?.icon && (
                <div className="bg-white min-w-[50px] min-h-[50px] rounded-xl flex items-center justify-center">
                  {card?.icon}
                </div>
              )}
            </div>
            <div className="flex flex-wrap justify-between items-center gap-2 w-full">
              {card.value !== null && card.value !== undefined && (
                <div className="flex-1 min-w-10 text-lg text-primaryText font-bold break-words overflow-wrap-anywhere" style={{ maxWidth: 'calc(100% - 220px)', wordBreak: 'break-word' }}>
                  {card.value}
                </div>
              )}
              <div className="relative flex-shrink-0 w-[190px] text-sm text-primaryText">
                {card.dropdownOptions && hasUpdatePermission ? (
                  card.isSearchable && card.title === "Locations" ? (
                    // Custom searchable dropdown for Locations card
                    <div className="relative city-dropdown-container">
                      {citiesLoading ? (
                        <div className="w-full px-2 py-1 text-base bg-transparent rounded-md border border-gray-200 flex items-center justify-center">
                          <svg
                            className="animate-spin h-4 w-4 text-gray-500"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        </div>
                      ) : (
                        <>
                          <div
                            className="w-full px-2 py-1 text-base bg-transparent cursor-pointer rounded-md border border-gray-200 flex items-center justify-between"
                            onClick={() => setShowCityDropdown(!showCityDropdown)}
                          >
                            <span className="truncate">
                              {selectedValues[card.title] || card.description}
                            </span>
                            <svg
                              className={`w-4 h-4 text-gray-500 transition-transform ${
                                showCityDropdown ? "rotate-180" : ""
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                          {showCityDropdown && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-hidden">
                              <div className="p-2 border-b border-gray-200">
                                <input
                                  type="text"
                                  placeholder="Search city..."
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  value={citySearchTerm}
                                  onChange={(e) => setCitySearchTerm(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              <div className="max-h-48 overflow-y-auto">
                                {filteredCities.length > 0 ? (
                                  filteredCities.map((city) => (
                                    <div
                                      key={city}
                                      className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
                                      onClick={() => {
                                        handleSelectChange(card.title, city);
                                        setShowCityDropdown(false);
                                        setCitySearchTerm("");
                                      }}
                                    >
                                      {city}
                                    </div>
                                  ))
                                ) : (
                                  <div className="px-3 py-2 text-sm text-gray-500">
                                    No cities found
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    // Regular select dropdown for other cards
                    <>
                      <select
                        className="w-full px-2 py-1  text-base bg-transparent cursor-pointer appearance-none rounded-md focus:outline-none truncate border border-gray-200"
                        value={selectedValues[card.title] || card.description}
                        onChange={(e) =>
                          handleSelectChange(card.title, e.target.value)
                        }
                      >
                        {card.dropdownOptions.map((option) => (
                          <option
                            className="outline-none focus:outline-none border-none"
                            key={option}
                            value={option}
                          >
                            {option}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </>
                  )
                ) : (
                  card.description
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DashboardHome;
